import { useCallback, useMemo } from "react";
import { t, Trans } from "@lingui/macro";
import {
  ErrorBoundary,
  GlobalHeader,
  postClose,
  useSnackbarContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { FancyIcon, WfIconNavigationCloseRegular } from "@WahooFitness/redesignr";
import { WarningAlt } from "@carbon/icons-react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import {
  WahooWorkoutType,
  WahooWorkoutTypeFamily,
  WahooWorkoutTypeLocation,
} from "@WahooFitness/cloud-client-types";
import { getWorkoutTypeNames } from "../WorkoutProfiles/AddWorkoutProfile/WorkoutTypeNames";
import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import { convertWorkoutFamilyToWorkoutType } from "@/services/workoutFamilyToWorkoutTypeConverter";

function CreateDefaultWorkoutProfile({
  workoutTypeFamily,
  workoutLocation,
}: {
  workoutTypeFamily: WahooWorkoutTypeFamily;
  workoutLocation: WahooWorkoutTypeLocation;
}) {
  const { enqueueSnackbar } = useSnackbarContext();
  const { createProfile } = useWorkoutProfilesContext();
  const closeAction = useCallback(() => {
    postClose("create_default_workout_profile");
  }, []);
  const workoutType = useMemo(() => {
    return convertWorkoutFamilyToWorkoutType(workoutTypeFamily);
  }, [workoutTypeFamily]);

  const profileDisplayName = useMemo(
    () => getProfileDisplayName(workoutType, workoutLocation),
    [workoutType, workoutLocation]
  );

  const handleCreateProfile = useCallback(async () => {
    const result = await createProfile(workoutType, profileDisplayName, {
      workout_type_location_id: workoutLocation,
      enable_gps: workoutLocation === WahooWorkoutTypeLocation.Outdoor ? true : false,
    });
    if (result) {
      postClose("create_default_workout_profile");
    } else {
      enqueueSnackbar({
        message: t`Unable to create a new workout profile.  Please try again.`,
        severity: "error",
      });
    }
  }, [createProfile, workoutType, profileDisplayName, enqueueSnackbar, workoutLocation]);

  return (
    // wf-self-scrolling-page suppresses the shell's page-end spacer: this page fills
    // the scrollport and pads its own bottom content, so the spacer would lift it twice.
    <Container
      className="wf-self-scrolling-page"
      maxWidth="sm"
      disableGutters
      sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}
    >
      <ErrorBoundary isLoading={false} onReload={() => {}} showError>
        <GlobalHeader
          title={t`Create default profile`}
          primaryLeftActionItem={{
            icon: <WfIconNavigationCloseRegular />,
            action: closeAction,
          }}
        />
        {/* Grows instead of height="100%": the container only has a min-height, so a
            percentage height would resolve to auto and collapse the flex chain that
            pins the buttons to the bottom. */}
        <Box display="flex" flexDirection="column" width="100%" flexGrow={1}>
          <Stack
            direction="column"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            p={2}
            // The buttons sit at the bottom of this grown Stack, so it needs the
            // safe-area clearance itself (set by the shared shell)
            pb="calc(var(--wf-bottom-safe-area, 0px) + 16px)"
            textAlign="center"
            width="100%"
            flexGrow={1}
          >
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              spacing={2}
              flexGrow={1}
            >
              <FancyIcon icon={<WarningAlt />} size="medium" color="warning" />
              <Typography variant="ui-lg-medium">
                <Trans>Can&apos;t launch workout</Trans>
              </Typography>
              <Typography variant="prose-md">
                <Trans>
                  To launch this workout, you need a workout profile for: {profileDisplayName}.
                </Trans>
              </Typography>
              <Typography variant="prose-md">
                <Trans>
                  Don&apos;t worry, we&apos;ll create one for you with default settings so you can
                  get going.
                </Trans>
              </Typography>
            </Stack>
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              spacing={2}
              width="100%"
            >
              <Button
                variant="contained"
                size="large"
                color="primary"
                fullWidth
                onClick={handleCreateProfile}
              >
                <Trans>Create profile and continue</Trans>
              </Button>
              <Button
                variant="outlined"
                size="large"
                color="primary"
                fullWidth
                onClick={closeAction}
              >
                <Trans>Cancel</Trans>
              </Button>
            </Stack>
          </Stack>
        </Box>
      </ErrorBoundary>
    </Container>
  );
}

function getProfileDisplayName(
  workoutType: WahooWorkoutType,
  workoutLocation: WahooWorkoutTypeLocation
) {
  const workoutTypeDisplayName = getWorkoutTypeNames()[WahooWorkoutType[workoutType]];
  const locationDisplayName =
    workoutLocation === WahooWorkoutTypeLocation.Indoor ? t`Indoor` : t`Outdoor`;
  return `${locationDisplayName} ${workoutTypeDisplayName}`;
}

export default CreateDefaultWorkoutProfile;
